import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Pager from './Paging';
import authService from './api-authorization/AuthorizeService';

interface JobNoteProps {
    jobId: number;
}

interface GenericList {
    id: number;
    description: string;
}

interface JobNotes {
    noteTypeId: number;
    newNotes: string;
    noteTypeFilter: string;
    noteItemFilter: number;
    showSystemNotes: boolean;
    showUserNotes: boolean;
    page: number;
    noteDetails: NoteDetails;
    noteTypes: GenericList[];
    noteItems: GenericList[];
    errors: any;
    showAddNote: boolean;
    loading: boolean;
    gridLoading: boolean;
}

interface NoteDetails {
    pageSize: number;
    totalRecords: number;
    jobNotes: NoteList[];
}

interface NoteList {
    id: number,
    type: string,
    item: string,
    dateFormatted: string,
    notes: string,
    addedBy: string,
    systemNote: boolean
}

export default class JobNote extends React.Component<JobNoteProps, JobNotes> {
    constructor(props: JobNoteProps) {
        super(props);
        this.state = {
            noteTypeId: -1,
            newNotes: "",
            showSystemNotes: false,
            showUserNotes: false,
            noteTypeFilter: "",
            noteItemFilter: -1,
            page: 1,
            noteDetails: {
                pageSize: 1,
                totalRecords: 0,
                jobNotes: []
            },
            noteTypes: [],
            noteItems: [],
            errors: {},
            showAddNote: false,
            loading: true,
            gridLoading: true
        }
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Job/GetNoteDropdowns?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ noteTypes: res.data.noteTypes, noteItems: res.data.noteItems });
        })

        this.getNotes(1, true, this.state.noteTypeFilter, this.state.noteItemFilter, this.state.showSystemNotes, this.state.showUserNotes);
    }

    async getNotes(page: number, getTotalRecords: boolean, filter: string, filterItemId: number, systemNotes: boolean, userNotes: boolean) {
        const token = await authService.getAccessToken();
        //get list of notes from database
        var totalRecords = getTotalRecords ? 0 : this.state.noteDetails.totalRecords;
        var noteType = filter != "" ? filter : 0;
        axios.get('api/Job/GetNotes?jobId=' + this.props.jobId + '&page=' + page + '&totalRecords=' + totalRecords + '&getTotalRecords=' + getTotalRecords + '&showSystemNotes=' + systemNotes + '&showUserNotes=' + userNotes + '&noteType=' + noteType + '&noteItemId=' + filterItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ page: page, noteDetails: res.data, loading: false, gridLoading: false });
        })
    }

    validate = () => {
        let fields = this.state;
        let errors: any = {};
        let formIsValid = true;

        if (fields.noteTypeId <= 0) {
            formIsValid = false;
            errors["noteTypeId"] = "Note Type is required";
        }

        if (!fields.newNotes || fields.newNotes.length < 5) {
            formIsValid = false;
            errors["newNotes"] = "Notes > 5 characters are required";
        }

        this.setState({ errors: errors });
        return formIsValid;
    }

    submit = (e: any) => {
        e.preventDefault();
        if (this.validate()) {
            //submit form
            this.saveJobNote();
        } else {
            toast.error("Fix validation errors before saving note!");
        }

    }

    async saveJobNote() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let jobNotes = {
            jobId: this.props.jobId,
            noteTypeId: this.state.noteTypeId,
            note: this.state.newNotes,
            subId: user.sub
        }

        axios.post('api/Job/SaveJobNote', jobNotes, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            toast.success("Note Saved");
            //then need to clear notes so can resave and add note to list
            var newNote = {
                id: res.data.id,
                type: res.data.noteType,
                item: "",
                dateFormatted: res.data.dateFormatted,
                notes: res.data.note,
                addedBy: res.data.addedBy,
                systemNote: res.data.systemNote
            };
            var noteDetails = this.state.noteDetails;
            if (this.state.page == 1) {
                //add note to top of list
                noteDetails.jobNotes.unshift(newNote);
                if (noteDetails.jobNotes.length > (this.state.noteDetails.pageSize)) {
                    //remove the oldest note as goes onto 2nd page
                    noteDetails.jobNotes.pop();
                }
            }
            this.setState({
                noteDetails: noteDetails, noteTypeId: -1, newNotes: "", showAddNote: false
            });
        })
        .catch(error => {
            var errors = error.response.data as any[];
            errors.map(function (error: any) {
                toast.error(error);
            });
        });
    }

    handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errors = this.state.errors;
        if (e.target.value) {
            delete errors[e.target.name];
        }

        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = errors;
        this.setState(change);
    }

    handleSysNotesChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.checked;
        this.setState(change);
        this.setState({ showUserNotes: false });    //reset user notes if changing system ones
        this.getNotes(1, true, this.state.noteTypeFilter, this.state.noteItemFilter, e.target.checked, false);
    }

    handleUserNotesChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.checked;
        this.setState(change);
        this.setState({ showSystemNotes: false });    //reset system notes if changing user ones
        this.getNotes(1, true, this.state.noteTypeFilter, this.state.noteItemFilter, false, e.target.checked);
    }

    handleTypeChange = (e: any) => {
        this.setState({ noteTypeFilter: e.target.value });
        this.getNotes(1, true, e.target.value, this.state.noteItemFilter, this.state.showSystemNotes, this.state.showUserNotes);
    }

    handleItemChange = (e: any) => {
        this.setState({ noteItemFilter: e.target.value });
        this.getNotes(1, true, this.state.noteTypeFilter, e.target.value, this.state.showSystemNotes, this.state.showUserNotes);
    }

    pageChanged = (page: number) => {
        this.setState({ page: page });
        this.getNotes(page, false, this.state.noteTypeFilter, this.state.noteItemFilter, this.state.showSystemNotes, this.state.showUserNotes);
    }

    showAddNote = () => {
        var showNote = !this.state.showAddNote;
        this.setState({ showAddNote: showNote });
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderNotes();


        return (<div>
            {contents}
        </div>);
    }

    renderNotes = () => {
        let filteredNoteTypes = this.state.noteTypes.filter(type =>
            type.id > 0
        )
        let filterItems = this.state.noteItems;

        var pagerContents;
        if ((this.state.noteDetails.totalRecords / this.state.noteDetails.pageSize ) > 1) {
            //only render if we have records
            pagerContents = <Pager page={this.state.page} pageSize={this.state.noteDetails.pageSize} totalRecords={this.state.noteDetails.totalRecords} pageChanged={this.pageChanged} />
        }

        return (
            <div>
                <div className={this.state.showAddNote ? "hidden" : ""}>
                    <button className="defaultbutton defaultbutton__small" onClick={this.showAddNote} type="button">Add Note</button>
                </div>
                <div className={this.state.showAddNote ? "" : "hidden" }>
                        <label className="input-group" htmlFor="noteType">
                            <span className="label">Type </span>
                            <select className="select" id="noteType" name="noteTypeId" value={this.state.noteTypeId} onChange={(e) => this.handleChange(e)}>
                                <option hidden defaultValue="-1"></option>
                                {filteredNoteTypes.map(type =>
                                    <option key={type.id} value={type.id}>{type.description}</option>
                                )};
                            </select>
                        </label>
                        <span className="label errors">{this.state.errors["noteTypeId"]}</span>
                        <label className="input-group" htmlFor="newNotes">
                        <span className="label">Notes</span>
                        <textarea className="input job__noteSize" id="newNotes" name="newNotes" value={this.state.newNotes} onChange={(e) => this.handleChange(e)}></textarea>
                        
                        </label>
                        <span className="label errors">{this.state.errors["newNotes"]}</span>
                    <div>
                        <button className="defaultbutton-prev" onClick={this.submit} type="button">Add</button>
                        <button className="defaultbutton-next" onClick={this.showAddNote} type="button">Cancel</button>
                    </div>
                </div>
                <div>
                    <hr></hr>
                </div>
                <div className="makeitflex">
                    <label className="input-group--inline" htmlFor="noteTypeFilter">
                        <span className="label">Type </span>
                        <select className="select" id="noteTypeFilter" name="noteTypeFilter" value={this.state.noteTypeFilter} onChange={(e) => this.handleTypeChange(e)}>
                            <option defaultValue="-1"></option>
                            {filteredNoteTypes.map(type =>
                                <option key={type.id} value={type.id}>{type.description}</option>
                            )};
                        </select>
                    </label>
                    <label className="input-group--inline" htmlFor="noteItemFilter">
                        <span className="label">Item </span>
                        <select className="select" id="noteItemFilter" name="noteItemFilter" value={this.state.noteItemFilter} onChange={(e) => this.handleItemChange(e)}>
                            <option defaultValue="-1"></option>
                            {filterItems.map(item =>
                                <option key={item.id} value={item.id}>{item.description}</option>
                            )};
                        </select>
                    </label>
                    <label className="input-group--inline job__checkboxPosition" htmlFor="showSystemNotes">
                        <span className="label">Show System Notes </span>
                        <div>
                            <input type="checkbox" name="showSystemNotes" id="showSystemNotes" checked={this.state.showSystemNotes}  onChange={(e) => this.handleSysNotesChange(e)}></input>
                        </div>
                    </label>
                    <label className="input-group--inline job__checkboxPosition" htmlFor="showUserNotes">
                        <span className="label">Show User Notes </span>
                        <div>
                            <input type="checkbox" name="showUserNotes" id="showUserNotes" checked={this.state.showUserNotes} onChange={(e) => this.handleUserNotesChange(e)}></input>
                        </div>
                    </label>
                </div>

                <div className="job__noteGridSize overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th>Date/Time</th>
                                <th>Type</th>
                                <th>Item</th>
                                <th>Notes</th>
                                <th>Added By</th>
                            </tr>
                        </thead>
                        {this.state.noteDetails.jobNotes.map((note, index) =>
                            <tbody key={note.id}>
                                <tr>
                                    <td>{note.dateFormatted}</td>
                                    <td>{note.type}</td>
                                    <td>{note.item}</td>
                                    <td>{note.notes}</td>
                                    <td>{note.addedBy}</td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
                {pagerContents}
            </div>    
        )

    }
}
